import arrayShuffle from 'array-shuffle'
import {graphql} from 'gatsby'
import {CountryCode as PhoneCountryCode, parsePhoneNumberFromString} from 'libphonenumber-js'
import React, {FC, useReducer} from 'react'

import PhoneInput from '@/components/phone-input'
import {DemoFormFragment} from '@/graphql-types'
import {useIsCompanyEmail} from '@/hooks/use-is-company-email'
import useParams from '@/hooks/use-params'
import {CalendlyPageParams} from '@/templates/calendly/calendly'
import compactObject from '@/utils/compact-object'
import {scrollToTop} from '@/utils/scroll'
import Button from '@helios/shared/components/button'
import Checkbox from '@helios/shared/components/checkbox'
import Input from '@helios/shared/components/input'
import RichText from '@helios/shared/components/rich-text'
import Select from '@helios/shared/components/select'
import useFormErrors from '@helios/shared/hooks/use-form-errors'
import useNavigate from '@helios/shared/hooks/use-navigate'
import {useRecaptchaHubspotSubmit} from '@helios/shared/hooks/use-recaptcha-hubspot-submit'
import locales, {CountryCode} from '@helios/shared/locale/locales'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {useLocale} from '@helios/shared/providers/locale'
import {Text} from '@helios/shared/styles/typography'
import CompanySize from '@helios/shared/types/company-size'
import DiscoveredVia from '@helios/shared/types/discovered-via'
import {HubspotContactProps} from '@helios/shared/utils/create-hubspot-contact'

import * as s from '../lead-form.styles'
import * as tracking from '../tracking'

export const OTHER = 'Other' as const

export interface State {
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
    phoneCountryCode: PhoneCountryCode
    companySize: CompanySize | ''
    country: CountryCode | typeof OTHER | ''
    discoveredVia: DiscoveredVia | ''
    hasConsented: boolean
}

enum ActionType {
    SET_VALUE,
}

type SetValue<TKey extends keyof State> = {
    type: ActionType.SET_VALUE
    key: keyof State
    value: State[TKey]
}

type Action<TKey extends keyof State> = SetValue<TKey>

const reducer = <TKey extends keyof State>(state: State, action: Action<TKey>) => {
    switch (action.type) {
        case ActionType.SET_VALUE: {
            return {
                ...state,
                [action.key]: action.value,
            }
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

const validateInputs = (state: State, content: Props['content'], isValidEmail: boolean) => {
    type Error = {
        key: keyof State
        value: string
    }

    const errors: Error[] = []

    const parsedPhoneNumber = parsePhoneNumberFromString(state.phoneNumber, state.phoneCountryCode)
    const isValidPhoneNumber = !!parsedPhoneNumber?.isValid()

    if (!state.firstName.trim()) {
        errors.push({
            key: 'firstName',
            value: content.data.first_name_error,
        })
    }

    if (!state.lastName.trim()) {
        errors.push({
            key: 'lastName',
            value: content.data.last_name_error,
        })
    }

    if (!state.email.trim() || !isValidEmail) {
        errors.push({
            key: 'email',
            value: content.data.email_error,
        })
    }

    if (!state.phoneNumber.trim() || !isValidPhoneNumber) {
        errors.push({
            key: 'phoneNumber',
            value: content.data.phone_number_error,
        })
    }

    if (!state.companySize.trim()) {
        errors.push({
            key: 'companySize',
            value: content.data.company_size_error,
        })
    }

    if (!state.country.trim()) {
        errors.push({
            key: 'country',
            value: content.data.country_error,
        })
    }

    if (!state.hasConsented) {
        errors.push({
            key: 'hasConsented',
            value: content.data.has_consented_error,
        })
    }

    return errors
}

export enum RedirectType {
    GO_TO_THANK_YOU_SOLE_TRADER,
    GO_TO_CALENDLY,
    GO_TO_THANK_YOU,
}

type Navigate = ReturnType<typeof useNavigate>

const handleRedirectEffect = (state: State, navigate: Navigate) => (redirectType: RedirectType) => {
    switch (redirectType) {
        case RedirectType.GO_TO_CALENDLY: {
            if (state.country === 'Other' || state.country === '') {
                return
            }

            const calendlyPageParams: CalendlyPageParams = {
                name: `${state.firstName} ${state.lastName}`,
                email: state.email,
                country: state.country,
                companySize: state.companySize,
                source: 'demo-form',
            }

            const params = new URLSearchParams(compactObject(calendlyPageParams))

            return navigate(`/book-a-demo?${params}`)
        }

        case RedirectType.GO_TO_THANK_YOU_SOLE_TRADER: {
            return navigate('/thankyousoletrader')
        }

        case RedirectType.GO_TO_THANK_YOU: {
            return navigate('/thankyou')
        }

        default: {
            throw new Error('Unrecognized redirect type')
        }
    }
}

export const getRedirectType = (companySize: State['companySize'], country: State['country']) => {
    const isOfSizes = (sizes: State['companySize'][]) => sizes.includes(companySize)
    const isOfCountries = (countries: State['country'][]) => countries.includes(country)

    const {SoleTrader} = CompanySize

    if (isOfSizes([SoleTrader]) && !isOfCountries(['dk', 'se'])) {
        return RedirectType.GO_TO_THANK_YOU_SOLE_TRADER
    }

    if (!isOfCountries(['Other'])) {
        return RedirectType.GO_TO_CALENDLY
    }

    return RedirectType.GO_TO_THANK_YOU
}

export interface Props {
    hubspotProperties?: Partial<HubspotContactProps>
    redirectCallback?: () => void
    className?: string
    trackQualifyingLeads?: boolean
    content: {
        pre_title?: string
        title: string
    } & DemoFormFragment
}

export const DemoForm: FC<Props> = ({
    content,
    hubspotProperties,
    redirectCallback,
    className,
    trackQualifyingLeads,
}) => {
    const currentLocale = useLocale()
    const params = useParams()
    const {recaptchaHubspotSubmit, loading, recaptchaElement} = useRecaptchaHubspotSubmit()

    React.useEffect(() => {
        tracking.bigformReached({country: currentLocale.country, trackQualifyingLeads})
    }, [currentLocale.country, trackQualifyingLeads])

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        // Validate inputs
        const errors = validateInputs(state, content, isValidEmail)

        if (errors.length) {
            errors.forEach((error) => {
                setError(error.key, error.value)
            })

            return scrollToTop()
        }

        await recaptchaHubspotSubmit(state, hubspotProperties)

        tracking.bigformSubmitted(state)

        // Redirect user
        if (redirectCallback) {
            redirectCallback()
        } else {
            redirect(getRedirectType(state.companySize, state.country))
        }
    }

    const initialState: State = {
        firstName: '',
        lastName: '',
        email: params.get('email') || '',
        phoneNumber: '',
        phoneCountryCode: currentLocale.country.toUpperCase() as PhoneCountryCode,
        companySize: '',
        country: currentLocale.country,
        discoveredVia: '',
        hasConsented: false,
    }

    const [state, dispatch] = useReducer(reducer, initialState)
    const {errors, setError, clearError} = useFormErrors(state)
    const isValidEmail = useIsCompanyEmail(state.email || '')
    const navigate = useNavigate()
    const redirect = handleRedirectEffect(state, navigate)
    const {recaptcha_message} = useGlobalStrings()

    const setValue = <TKey extends keyof State>(key: TKey, value: State[TKey]) => {
        dispatch({
            type: ActionType.SET_VALUE,
            key,
            value,
        })
        tracking.bigformInteracted(key)
    }

    const companySizeOptions = Object.values(CompanySize).map((value) => {
        switch (value) {
            case CompanySize.SoleTrader: {
                return {
                    label: content.data.sole_trader,
                    value,
                }
            }

            case CompanySize.Size_1: {
                return {
                    label: `${value} ${content.data.employee}`,
                    value,
                }
            }

            default: {
                return {
                    label: `${value} ${content.data.employees}`,
                    value,
                }
            }
        }
    })

    const countryOptions = [
        ...locales.map((locale) => ({
            label: locale.countryName,
            value: locale.country,
        })),
        {
            label: content.data.other,
            value: OTHER,
        },
    ]

    const discoveredViaOptions = [
        {
            label: content.data.peer_recommendation,
            value: DiscoveredVia.PeerRecommendation,
        },
        {
            label: content.data.trust_pilot,
            value: DiscoveredVia.TrustpilotG2orCapterra,
        },
        {
            label: content.data.google_search,
            value: DiscoveredVia.GoogleSearchEngine,
        },
        {
            label: content.data.youtube,
            value: DiscoveredVia.YouTube,
        },
        {
            label: content.data.tv,
            value: DiscoveredVia.TV,
        },
        {
            label: content.data.outdoor_advertising,
            value: DiscoveredVia.OutdoorAdvertising,
        },
        {
            label: content.data.social_media,
            value: DiscoveredVia.SocialMedia,
        },
        {
            label: content.data.press,
            value: DiscoveredVia.Press,
        },
    ]

    const discoveredViaOptionsShuffled = arrayShuffle(discoveredViaOptions)

    const handleInputChange: typeof setValue = (key, value) => {
        clearError(key)
        setValue(key, value)
    }

    const errorSpacePlaceholder = (
        <Text size="xxs" font="secondary">
            {'\u00a0'}
        </Text>
    )

    return (
        <s.Form className={className} onSubmit={handleFormSubmit} noValidate>
            <s.Titles>
                <Text size="xs" font="secondary" color="muted" center>
                    {content.pre_title}
                </Text>

                <Text size="md" center>
                    {content.title}
                </Text>
            </s.Titles>

            <s.InputsWrapper>
                <s.Row>
                    <div>
                        <Input
                            label={content.data.first_name_label}
                            value={state.firstName}
                            onChange={(value) => handleInputChange('firstName', value)}
                            error={errors.firstName}
                        />

                        {!errors.firstName && errorSpacePlaceholder}
                    </div>

                    <div>
                        <Input
                            label={content.data.last_name_label}
                            value={state.lastName}
                            onChange={(value) => handleInputChange('lastName', value)}
                            error={errors.lastName}
                        />

                        {!errors.lastName && errorSpacePlaceholder}
                    </div>
                </s.Row>

                <s.Row>
                    <div>
                        <Input
                            type="email"
                            label={content.data.email_label}
                            value={state.email}
                            onChange={(value) => handleInputChange('email', value)}
                            error={errors.email}
                        />

                        {!errors.email && errorSpacePlaceholder}
                    </div>

                    <div>
                        <PhoneInput
                            label={content.data.phone_number_label}
                            setPhoneNumber={(value) => handleInputChange('phoneNumber', value)}
                            phoneNumber={state.phoneNumber}
                            setCountryCode={(value) => handleInputChange('phoneCountryCode', value)}
                            countryCode={state.phoneCountryCode}
                            error={errors.phoneNumber}
                        />

                        {!errors.phoneNumber && errorSpacePlaceholder}
                    </div>
                </s.Row>

                <s.Row>
                    <div>
                        <Select
                            label={content.data.company_size_label}
                            options={companySizeOptions}
                            placeholder={content.data.company_size_placeholder}
                            value={state.companySize}
                            onChange={(value) => handleInputChange('companySize', value)}
                            error={errors.companySize}
                        />

                        {!errors.companySize && errorSpacePlaceholder}
                    </div>

                    <div>
                        <Select
                            label={content.data.country_label}
                            options={countryOptions}
                            placeholder={content.data.country_placeholder}
                            value={state.country}
                            onChange={(value) => handleInputChange('country', value)}
                            error={errors.country}
                        />

                        {!errors.country && errorSpacePlaceholder}
                    </div>
                </s.Row>
                <s.Row>
                    <div>
                        <Select
                            label={content.data.discovered_via_label}
                            options={discoveredViaOptionsShuffled}
                            placeholder={content.data.discovered_via_placeholder}
                            value={state.discoveredVia}
                            onChange={(value) => handleInputChange('discoveredVia', value)}
                        />
                    </div>
                </s.Row>
            </s.InputsWrapper>

            <Checkbox
                label={content.data.consent_label.raw}
                checked={state.hasConsented}
                onChange={(value) => handleInputChange('hasConsented', value)}
                error={errors.hasConsented}
            />

            <Button loading={loading}>{content.data.demo_submit_text}</Button>

            <div>
                {recaptchaElement}

                <RichText size="xxs" font="secondary" color="muted" content={recaptcha_message} />
            </div>
        </s.Form>
    )
}

export const demoFormFragment = graphql`
    fragment DemoForm on PrismicBigform {
        data {
            first_name_label
            last_name_label
            email_label
            phone_number_label
            company_size_label
            company_size_placeholder
            country_label
            country_placeholder
            discovered_via_label
            discovered_via_placeholder
            consent_label {
                raw
            }
            other
            peer_recommendation
            trust_pilot
            google_search
            youtube
            tv
            outdoor_advertising
            social_media
            press
            sole_trader
            employee
            employees
            demo_submit_text
            first_name_error
            last_name_error
            email_error
            phone_number_error
            company_size_error
            country_error
            has_consented_error
        }
    }
`

export default DemoForm
